import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../../config/AxiosConfig';
import languages from '../../../helpers/utils/languages';
import BrandLogo from '../../utils/common/BrandLogo';
import LoadingButton from '../../utils/common/LoadingButton';
import '/node_modules/flag-icons/css/flag-icons.min.css';

const UpdateContactLanguageDialog = (props) => {
  const {
    openUpdateContactLanguageDialog,
    setOpenUpdateContactLanguageDialog,
    contact,
    mutate,
    setUpdatedLanguage,
  } = props;
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (selectedIndex === null && contact?.language) {
      const index = languages?.findIndex(
        (lang) => lang.code2 === contact?.language
      );
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [contact, selectedIndex]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const changeContactLanguage = () => {
    setIsLoadingTranslation(true);
    AxiosConfig.create('put', `/contacts/${contact?._id}`)
      .call({
        language: languages[selectedIndex].code2,
      })
      .exit((res) => {
        mutate();
        contact.language = res.data.language;
        setUpdatedLanguage(res.data.language.toLowerCase());
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setOpenUpdateContactLanguageDialog(false);
        setOpen(false);
        setIsLoadingTranslation(false);
      });
  };

  return (
    <>
      <Dialog open={openUpdateContactLanguageDialog}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          Langue du contact
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '500px',
          }}
        >
          <ButtonGroup
            variant="outlined"
            ref={anchorRef}
            color="primary"
            aria-label="Langues"
            sx={{ height: '2.5rem' }}
          >
            <LoadingButton
              isLoading={isLoadingTranslation}
              onClick={changeContactLanguage}
              disabled={selectedIndex === null}
            >
              {selectedIndex !== null ? (
                <>
                  Changer la langue en {languages[selectedIndex]?.lang}{' '}
                  <AutoFixHighIcon />
                </>
              ) : (
                'Pas de langue'
              )}
            </LoadingButton>
            <Button
              size="small"
              aria-controls={open ? 'split-button-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-label="Selectionner une nouvelle langue pour le contact"
              aria-haspopup="menu"
              onClick={handleToggle}
            >
              <KeyboardArrowDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{
              zIndex: 10000,
              height: '25rem',
              overflowY: 'scroll',
              boxShadow: '1px 1px 10px grey',
            }}
            disablePortal={false}
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement={'right'}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {languages.map((lang, index) => (
                    <MenuItem
                      key={lang.code2}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      <span className={`fi fi-${lang?.flag}`} /> - {lang?.lang}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
          <Button
            onClick={() => {
              setOpenUpdateContactLanguageDialog(false);
            }}
            color="warning"
            size="small"
            variant="outlined"
            sx={{ marginTop: '20px' }}
          >
            Annuler
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const MenuStoreItem = ({
  contactStore,
  stores,
  selectedStore,
  handleMenuItemClick,
}) => {
  const renderItems = (items) => {
    return items?.map((option) => (
      <MenuItem
        key={option._id}
        selected={selectedStore?._id === option?._id}
        onClick={(event) => handleMenuItemClick(event, option)}
      >
        {option.name}
      </MenuItem>
    ));
  };
  switch (contactStore) {
    case 'Inconnu':
      return renderItems(stores);
    case 'Allegro': {
      const otherSellers = stores?.filter(
        (option) => option.seller.name !== 'Hoortrade'
      );
      return renderItems(otherSellers);
    }
  }
};

const ContactDetails = ({ contact, handleCopyContent, mutate }) => {
  const stores = useSelector((state) => state?.static?.stores);
  const [openUpdateContactLanguageDialog, setOpenUpdateContactLanguageDialog] =
    useState(false);
  const [updatedLanguage, setUpdatedLanguage] = useState(
    contact?.language?.toLowerCase()
  );
  const [hasChanged, setHasChanged] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const editableStores = ['Allegro', 'Inconnu'];
  const [selectedStore, setSelectedStore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setUpdatedLanguage(contact?.language?.toLowerCase());
    setSelectedStore(stores.find((s) => s._id === contact?.store?._id));
  }, [contact, stores]);

  const handleStoreChange = () => {
    setIsLoading(true);
    AxiosConfig.create('put', `/contacts/${contact._id}/updateStore`)
      .call({
        storeId: selectedStore?._id,
      })
      .exit((res) => {
        mutate();
        contact.store = res.data.store;
        setSelectedStore(stores.find((s) => s._id === res.data.store._id));
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMenuItemClick = (event, action) => {
    setSelectedStore(action);
    setHasChanged(true);
    setOpen(false);
  };

  return (
    <>
      <Typography component={'span'} sx={{ fontSize: 20 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'right',
            justifyContent: 'space-between',
          }}
        >
          <Box
            onClick={() => {
              handleCopyContent(
                'Nom',
                `${contact?.firstName} ${contact?.lastName}`
              );
            }}
          >
            {contact?.firstName !== null && contact?.lastName !== null
              ? `${contact?.firstName} ${contact?.lastName}`
              : contact?.lastName !== null
                ? `${contact?.lastName}`
                : `${contact?.email}`}
          </Box>
          <Box sx={{ marginLeft: 5 }}>
            {contact?.store?.contactType?.name === 'Professionnel' && (
              <Badge badgeContent="PRO" color="error" />
            )}
            {contact?.store?.contactType?.name === 'Particulier' && (
              <Badge badgeContent="PART" color="primary" />
            )}
            {contact?.store?.contactType?.name === 'Marketplace' && (
              <Badge badgeContent="MP" color="secondary" />
            )}
          </Box>
        </Box>
      </Typography>
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          marginLeft: 1,
          marginRight: 1,
        }}
      >
        <Grid>
          <Grid container sx={{ fontSize: 12 }}>
            <Grid
              onClick={() => {
                handleCopyContent('Email', `${contact?.email}`);
              }}
            >
              Email: {' ' + contact?.email}
            </Grid>
          </Grid>
          <Grid container sx={{ fontSize: 12 }}>
            <Grid>
              Téléphone:{' '}
              {contact?.phone
                ? `+${contact?.store?.country.dialingCode} ${contact?.phone}`
                : 'Non renseigné'}
            </Grid>
          </Grid>
          <Grid container sx={{ fontSize: 12 }}>
            <Grid>
              Genre:{' '}
              {contact?.gender === 'Homme'
                ? 'M'
                : contact?.gender === 'Femme'
                  ? 'F'
                  : 'Non renseigné'}
            </Grid>
          </Grid>
          <Grid container sx={{ fontSize: 12 }}>
            <Grid>
              <Typography component={'span'} sx={{ fontSize: 12 }}>
                {'Créé le '}
                {new Date(contact?.createdAt).toLocaleString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            {editableStores.includes(contact?.store?.shortName) ? (
              <>
                <ButtonGroup
                  variant="outlined"
                  ref={anchorRef}
                  sx={{ width: '100%' }}
                  color="info"
                >
                  <LoadingButton
                    isLoading={isLoading}
                    onClick={handleStoreChange}
                  >
                    {!hasChanged
                      ? contact?.store?.shortName
                      : selectedStore.shortName}
                  </LoadingButton>
                  <Button
                    size="small"
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="menu"
                    onClick={handleToggle}
                  >
                    <KeyboardArrowDownIcon />
                  </Button>
                </ButtonGroup>
                <Popper
                  sx={{ zIndex: 1 }}
                  open={open}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  transition
                  disablePortal
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === 'bottom'
                            ? 'center top'
                            : 'center bottom',
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleClose}>
                          <MenuList
                            id="split-button-menu"
                            autoFocusItem
                            sx={{ height: 260, overflowY: 'scroll' }}
                          >
                            <MenuStoreItem
                              contactStore={contact?.store?.shortName}
                              stores={stores}
                              selectedStore={selectedStore}
                              handleMenuItemClick={handleMenuItemClick}
                            />
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </>
            ) : (
              <Typography component={'span'} sx={{ fontSize: 16 }}>
                {contact?.store?.shortName}
              </Typography>
            )}
          </Grid>
          <Grid
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Box sx={{ display: 'flex' }}>
              <Tooltip
                placement="right"
                title={`${contact?.store?.country?.name}`}
              >
                <Typography component={'span'}>
                  Pays
                  <span
                    className={`fi fi-${contact?.store?.country?.code2?.toLowerCase()}`}
                  ></span>
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
          <Grid
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Box sx={{ display: 'flex' }}>
              <Tooltip
                placement="right"
                title={`${
                  languages.find(
                    (language) =>
                      language.code2.toLowerCase() === updatedLanguage
                  )?.lang
                }`}
              >
                <>
                  <Typography component={'span'}>
                    Langue
                    <span
                      className={`fi fi-${
                        languages.find(
                          (language) =>
                            language.code2.toLowerCase() === updatedLanguage
                        )?.flag
                      }`}
                    ></span>
                  </Typography>
                  <IconButton
                    onClick={() => {
                      setOpenUpdateContactLanguageDialog(true);
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </>
              </Tooltip>
            </Box>
          </Grid>
          <Typography
            component={'span'}
            sx={{ fontSize: 12 }}
            color="text.secondary"
          >
            {contact?.reference ? contact?.reference : 'N/A'}
          </Typography>
        </Grid>
      </Grid>
      <UpdateContactLanguageDialog
        openUpdateContactLanguageDialog={openUpdateContactLanguageDialog}
        setOpenUpdateContactLanguageDialog={setOpenUpdateContactLanguageDialog}
        contact={contact}
        mutate={mutate}
        setUpdatedLanguage={setUpdatedLanguage}
      />
      <ContactAddress addresses={contact?.addresses} />
    </>
  );
};

const ContactCompany = (props) => {
  const { company, _handleCopyContent } = props;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography component={'span'} sx={{ fontSize: 20 }}>
            {company?.tradeName}
          </Typography>
          <Typography component={'span'} sx={{ fontSize: 12, marginLeft: 2 }}>
            {company?.reference}
          </Typography>
        </Box>
        <Typography component={'span'} sx={{ fontSize: 12 }}>
          {company?.legalStatus} {company?.name} {company?.intracomVAT}
        </Typography>
        <Typography component={'span'} sx={{ fontSize: 12 }}>
          {company?.status}
        </Typography>
        <Typography component={'span'} sx={{ fontSize: 12 }}>
          {company?.leaderFirstName} {company?.leaderLastName}
        </Typography>
        <Typography component={'span'} sx={{ fontSize: 12 }}>
          {company?.phone}
        </Typography>
      </Box>
      <ContactAddress addresses={company?.addresses} />
    </>
  );
};

const ContactAddress = (props) => {
  const { addresses } = props;

  return (
    <>
      <Typography component={'h6'} sx={{ marginTop: 2 }}>
        Adresses
      </Typography>
      <Divider sx={{ marginBottom: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        {addresses?.map((address, index) => {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                marginLeft: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginLeft: 2,
                  alignItems: 'start',
                }}
              >
                <Chip
                  variant="outlined"
                  color={address?.isBilling ? 'primary' : 'error'}
                  label={address?.isBilling ? 'Facturation' : 'Livraison'}
                  sx={{
                    marginRight: 2,
                    marginBottom: 2,
                  }}
                />
                <Typography
                  component={'span'}
                  sx={{ fontSize: 12, display: 'flex' }}
                >
                  {address?.line1}
                </Typography>
                <Typography
                  component={'span'}
                  sx={{ fontSize: 12, display: 'flex' }}
                >
                  {address?.line2}
                </Typography>
                <Typography
                  component={'span'}
                  sx={{ fontSize: 12, display: 'flex' }}
                >
                  {address?.zipCode}, {address?.city}
                </Typography>
                <Typography
                  component={'span'}
                  sx={{ fontSize: 12, display: 'flex' }}
                >
                  {address?.country?.name}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

const Contact = (props) => {
  const { contact, mutate } = props;
  const [tooltipContent, setTooltipContent] = useState(false);

  const handleCopyContent = (type, content) => {
    navigator.clipboard.writeText(content);
    setTooltipContent(`${type} copié.`);
    setTimeout(() => {
      setTooltipContent('');
    }, 2000);
  };

  return (
    <>
      <Card>
        <Tooltip title={tooltipContent} followCursor>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'right',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <CardContent sx={{ marginRight: 2 }}>
              <ContactDetails
                contact={contact}
                handleCopyContent={handleCopyContent}
                mutate={mutate}
              />
            </CardContent>
            {contact?.company && <Divider variant="middle" flexItem />}
            {contact?.company && (
              <CardContent>
                <ContactCompany
                  company={contact?.company}
                  handleCopyContent={handleCopyContent}
                />
              </CardContent>
            )}
          </Box>
        </Tooltip>
      </Card>
    </>
  );
};

const Contacts = (props) => {
  const { contacts, mutate } = props;
  const [storeTab, setStoreTab] = useState(0);

  const handleStoreTabChange = (event, value) => {
    setStoreTab(value);
  };

  return (
    <Paper elevation={24} sx={{ width: '48vw' }}>
      <Tabs value={storeTab} onChange={handleStoreTabChange} centered={true}>
        {contacts &&
          contacts?.map((contact, index) => {
            return (
              <Tab
                key={index}
                label={
                  <Box>
                    <BrandLogo
                      brand={contact?.store?.brand?.name}
                      type={contact?.store?.contactType?.codeBO}
                      style={{ width: 'inherit' }}
                    />
                  </Box>
                }
                style={{
                  width: 250,
                }}
                value={index}
              />
            );
          })}
      </Tabs>
      <Contact contact={contacts[storeTab]} mutate={mutate} />
    </Paper>
  );
};

export default Contacts;
